/**
 * AmbientParticles.tsx
 *
 * Decorative full-screen canvas that renders softly drifting particles in the
 * background of the application.
 *
 * ## Rendering
 * - A `<canvas>` element is fixed to the viewport (`position: fixed; inset: 0`)
 *   and sits at `z-index: 0` so it never intercepts pointer events.
 * - Up to 25 particles are alive at any time; new ones spawn at the bottom of
 *   the canvas and drift upward with a slight horizontal wobble.
 * - Each particle fades in over the first 20 % of its lifetime, remains fully
 *   visible until 80 %, then fades out.
 * - Particles younger than 50 % of their lifespan are rendered in a muted
 *   purple (`#8b4a6b`); older particles shift to gold (`#c9a84c`), echoing the
 *   application's arcane colour palette.
 * - The canvas is resized to match the viewport on mount and whenever the
 *   window is resized.
 * - The animation loop is cancelled and the resize listener removed on unmount.
 *
 * This component has no props and renders no visible HTML — only the canvas.
 */

'use client';

import React, { useEffect, useRef } from 'react';

/**
 * Internal data structure for a single ambient particle.
 *
 * @property x        - Current horizontal position in canvas pixels.
 * @property y        - Current vertical position in canvas pixels.
 * @property size     - Radius of the particle circle in pixels.
 * @property speedY   - Vertical velocity (negative = upward drift).
 * @property speedX   - Horizontal velocity (positive or negative wobble).
 * @property opacity  - Current opacity, computed from `life / maxLife`.
 * @property life     - Number of animation frames elapsed since spawn.
 * @property maxLife  - Total lifespan in animation frames before the particle is removed.
 */
interface Particle {
  x: number;
  y: number;
  size: number;
  speedY: number;
  speedX: number;
  opacity: number;
  life: number;
  maxLife: number;
}

export default function AmbientParticles() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const spawnParticle = (): Particle => ({
      x: Math.random() * canvas.width,
      y: canvas.height + 10,
      size: Math.random() * 1.5 + 0.5,
      speedY: -(Math.random() * 0.4 + 0.1),
      speedX: (Math.random() - 0.5) * 0.2,
      opacity: 0,
      life: 0,
      maxLife: Math.random() * 200 + 150,
    });

    // Seed initial particles
    for (let i = 0; i < 18; i++) {
      const p = spawnParticle();
      p.y = Math.random() * canvas.height;
      p.life = Math.random() * p.maxLife;
      particlesRef.current.push(p);
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Occasionally spawn new
      if (Math.random() < 0.03 && particlesRef.current.length < 25) {
        particlesRef.current.push(spawnParticle());
      }

      particlesRef.current = particlesRef.current.filter(p => p.life < p.maxLife);

      for (const p of particlesRef.current) {
        p.life += 1;
        p.x += p.speedX;
        p.y += p.speedY;

        const lifeRatio = p.life / p.maxLife;
        p.opacity = lifeRatio < 0.2
          ? lifeRatio / 0.2
          : lifeRatio > 0.8
          ? (1 - lifeRatio) / 0.2
          : 1;

        ctx.save();
        ctx.globalAlpha = p.opacity * 0.35;
        ctx.fillStyle = lifeRatio > 0.5 ? '#c9a84c' : '#8b4a6b';
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resize);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      aria-hidden="true"
    />
  );
}